import {
	ApiAuthenticationError,
	ApiNetworkError,
	ApiServiceError,
	ApiVersionMismatchError
} from '@adminsoft/ui/types/api';
import { toast } from '$lib/toast';
import { t } from '$lib/i18n';
import { errorDataInterpolation, networkErrorMessage } from '$lib/utils/error';

type ApiError =
	| ApiServiceError
	| ApiAuthenticationError
	| ApiVersionMismatchError
	| ApiNetworkError;

export function isApiError(error: unknown): error is ApiError {
	return (
		error instanceof ApiServiceError ||
		error instanceof ApiAuthenticationError ||
		error instanceof ApiVersionMismatchError ||
		error instanceof ApiNetworkError
	);
}

const HANDLED = Symbol('apiErrorHandled');

export function handleApiError(error: ApiError): void {
	const tagged = error as unknown as Record<symbol, boolean>;
	if (tagged[HANDLED]) {
		return;
	}
	tagged[HANDLED] = true;

	if (error instanceof ApiAuthenticationError) {
		return;
	}
	if (error instanceof ApiVersionMismatchError) {
		toast.error(t('newApplicationUpdate'));
		return;
	}
	if (error instanceof ApiServiceError && error.errorCode === 'AuthorizationError') {
		const missing = extractMissingPermissions(error.errorData);
		if (missing.length > 0) {
			const labels = missing.map((p) => t(`permission-${p}`).toUpperCase());
			toast.error(t('detailedAuthorizationError'), labels.map((l) => `• ${l}`).join('\n'));
		} else {
			toast.error(t('AuthorizationError'));
		}
		return;
	}
	if (error instanceof ApiNetworkError) {
		toast.error(networkErrorMessage());
		return;
	}
	toast.error(t(error.errorCode, errorDataInterpolation(error.errorData)));
}

function extractMissingPermissions(errorData: unknown): string[] {
	if (errorData == null || typeof errorData !== 'object') {
		return [];
	}
	const value = (errorData as { missingPermissions?: unknown }).missingPermissions;
	if (!Array.isArray(value)) {
		return [];
	}
	return value.filter((x): x is string => typeof x === 'string');
}
